<template>
  <div class="prods-edit">
    <h2>Recipe</h2>
    <form @submit.prevent>
      <label for="recipe">Name</label>
      <input type="text" name="recipe" v-model="currRecipe.value.escaped" />
      <label for="category">Category</label>
      <input list="category" name="category" v-model="currRecipe.value.name" />
      <datalist id="category">
        <option v-for="category in cogsRecipeCategories" v-bind:key="category">{{ category.key[0] }}</option>
      </datalist>
      <div v-if="recipeType == 'prep'" class="unit-serving">
        <div id="cost-title">
          <div class="left-align unit" @click.stop.prevent="handleUnitChoice('uom')">
            <div class="unit-block">
              <label for="uom" class="one-label">UOM </label>
              <input list="uoms" name="uom" v-model="currRecipe.value.UOM" />
              <datalist id="uoms">
                <option v-for="unit in cogsUOM" v-bind:key="unit.key">{{ unit.key }}</option>
              </datalist>
            </div>
            <div class="unit-block">
              <label for="quantity" class="one-label">Quantity </label>
              <input type="number" name="quantity" v-model="currRecipe.value.uom_quantity" />
            </div>
          </div>
          <span id="cost"> OR </span>
          <div class="right-align unit" @click.stop.prevent="handleUnitChoice('servings')">
            <div class="unit-block">
              <label for="uom" class="one-label"># servings </label>
              <input list="uoms" name="uom" v-model="currRecipe.value.servings" />
            </div>
          </div>
        </div>
      </div>
      <div class="cost-title">
        <label for="type">Ingredients</label><span id="cost">Cost: <CurrencyFormatter :value="costing[1]" :currency="companySettings.currency" /></span>
      </div>
      <div class="list-container">
        <div v-for="(ingredient, idx) in currRecipe.value.ingredients" :key="ingredient" class="ingredient-btn">
          <div id="recipe-ing">
            <div class="ingredient" @click="goToItem(ingredient, 'ingredient')">
              <p>{{ findIngredientByID(ingredient.id).key }} - {{ ingredient.usage }}{{ ingredient.uom }}</p>
              <p class="subtitle">
                {{ findIngredientByID(ingredient.id).value.name }}
              </p>
            </div>
            <CurrencyFormatter :value="costing[0][idx].price" :currency="companySettings.currency" />
            <div v-if="checkIngredientUOM(ingredient, 'isIngredient')">
              <span>{{ standardUOMRelationship.uom }} = {{ standardUOMRelationship.value }}{{ ingredient.uom }}</span>
            </div>
            <div v-else>
              <span v-if="findIngredientByID(ingredient.id).value.price_per_uom.split(' ')[2] !== ingredient.uom">
                1
                {{ findIngredientByID(ingredient.id).value.price_per_uom.split(" ")[2] }}
                =
                <input type="number" step="any" v-model="ingredient.uom_relationship" />{{ ingredient.uom }}
              </span>
            </div>
          </div>
          <font-awesome-icon icon="times" @click="removeIngredient(idx)" />
        </div>
      </div>
      <div class="cost-title">
        <label for="type">Sub Recipes</label><span id="cost">Cost: <CurrencyFormatter :value="costing[3]" :currency="companySettings.currency" /></span>
      </div>
      <div class="list-container">
        <div v-for="(parentRecipe, idx) in currRecipe.value.recipes" :key="parentRecipe" class="ingredient-btn">
          <div id="recipe-ing">
            <div class="ingredient" @click="goToItem(parentRecipe, 'sub-recipe')">
              <p>{{ findRecipeByID(parentRecipe.id).key[1] }}</p>
              <p class="subtitle" v-if="parentRecipe.usage && parentRecipe.uom">{{ parentRecipe.usage }}{{ parentRecipe.uom }}</p>
              <p class="subtitle" v-else>sub recipe</p>
            </div>
            <CurrencyFormatter :value="costing[2][idx].price" :currency="companySettings.currency" />
            <div v-if="checkIngredientUOM(parentRecipe, 'isRecipe')">
              <span>{{ standardUOMRelationship.uom }} = {{ standardUOMRelationship.value }}{{ parentRecipe.uom }}</span>
            </div>
            <div v-else>
              <span v-if="findRecipeByID(parentRecipe.id).value.costing_type == 'servings'">One serving </span>
              <span v-else-if="findRecipeByID(parentRecipe.id).value.price_per_uom.split(' ')[2] !== parentRecipe.uom">
                1
                {{ findRecipeByID(parentRecipe.id).value.price_per_uom.split(" ")[2] }}
                =
                <input type="number" v-model="parentRecipe.uom_relationship" />{{ parentRecipe.uom }}
              </span>
              <span v-else-if="findRecipeByID(parentRecipe.id).value.price_per_uom.split(' ')[2] == parentRecipe.uom">
                {{ findRecipeByID(parentRecipe.id).value.price_per_uom.split(" ")[2] }}</span
              >
            </div>
          </div>
          <font-awesome-icon icon="times" @click="removeRecipe(idx)" id="remove-icon" />
        </div>
      </div>
      <div class="actions">
        <div>
          <button @click.stop.prevent="clearForm" id="left">Clear Form</button>
          <button @click.stop.prevent="addToRecipe = true" id="add">Add Ingredient/Recipe</button>
        </div>
        <button v-if="!recipeAltered" type="button" @click="addNewRecipe" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          <span v-else>Add Recipe</span>
        </button>
        <button v-if="recipeAltered" type="button" @click="saveNewRecipe" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          <span v-else>Save Recipe</span>
        </button>
      </div>
    </form>
    <AddToRecipe :showModal="addToRecipe" @confirm="completeAddition" @close="closeAddModal" />
  </div>
</template>

<script>
import { api } from "@monolith/helpers";
import { costingCalc } from "@monolith/helpers";
import { CurrencyFormatter } from "@monolith/components";
import AddToRecipe from "./shared/AddToRecipe.vue";
import { cogsMixin } from "./shared/cogsMixin.js";

export default {
  name: "Recipe",
  components: { CurrencyFormatter, AddToRecipe },
  mixins: [cogsMixin],
  emits: ["recipe-saved", "saved"],
  data() {
    return {
      standardUOMRelationship: {
        value: 0,
        uom: "",
      },
      addToRecipe: false,
      saving: false,
      currRecipe: {
        value: {
          escaped: "",
          price_per_uom: "",
          name: "",
          type: this.recipeType,
          ingredients: [],
          recipes: [],
          UOM: "",
          costing_type: "",
          uom_quantity: 1,
        },
      },
      recipeAltered: false,
    };
  },
  props: {
    currentRecipe: {
      type: Object,
      default: () => ({}),
    },
    recipeType: {
      type: String,
      default: "menu",
    },
  },
  computed: {
    currRecipePrice() {
      return this.currRecipe.value.price_per_uom.split(" ")[0];
    },
    currRecipeUOM() {
      return this.currRecipe.value.price_per_uom.split(" ").slice(-1)[0];
    },
    costing() {
      // returns costings in an array, all calcs are gst excl
      return costingCalc.cost(this.currRecipe);
    },
    cost_per_serving() {
      return (this.costing[1] / this.currRecipe.value.servings).toFixed(2);
    },
  },

  methods: {
    handleUnitChoice(type) {
      if (type === "uom") {
        let el = document.querySelector(".left-align");
        el.classList.add("selected");
        el.classList.remove("opacity");
        let el2 = document.querySelector(".right-align");
        el2.classList.add("opacity");
        el2.classList.remove("selected");
      } else if (type === "servings") {
        let el = document.querySelector(".right-align");
        el.classList.add("selected");
        el.classList.remove("opacity");
        let el2 = document.querySelector(".left-align");
        el2.classList.add("opacity");
        el2.classList.remove("selected");
      }
    },

    checkIngredientUOM(ingredient, type) {
      let relation = "";
      if (type === "isIngredient") {
        relation = `${ingredient.uom.toLowerCase()}, ${this.findIngredientByID(ingredient.id).value.price_per_uom.split(" ")[2].toLowerCase()}`;
      } else {
        relation = `${this.findRecipeByID(ingredient.id).value.price_per_uom.split(" ")[2].toLowerCase()}`;
      }
      if (relation == "gr, kg" || relation == "ml, l" || relation == "kg" || relation == "l") {
        this.standardUOMRelationship.value = 1000;
        this.standardUOMRelationship.uom = "kg";
        return true;
      } else if (relation == "kg, gr" || relation == "l, ml") {
        this.standardUOMRelationship.value = 0.001;
        this.standardUOMRelationship.uom = "gr";
        return true;
      } else {
        return false;
      }
    },

    goToItem(item, type) {
      if (type === "ingredient") this.$router.push({ name: "cogs-products", params: { prod: item.id, loadProduct: true } });
      else if (type === "sub-recipe") this.$router.push({ name: "base-recipes", params: { recipeId: item.id, loadProduct: true } });
    },

    closeAddModal() {
      this.addToRecipe = false;
    },

    completeAddition(ingredient) {
      this.addToRecipe = false;
      if (ingredient.type === "ingredient") {
        this.loadIngredient(ingredient.item);
      } else if (ingredient.type === "sub-recipe") {
        this.loadSubRecipe(ingredient.item);
      }
    },

    clearForm() {
      this.currRecipe = {
        value: {
          escaped: "",
          price_per_uom: "",
          name: "",
          type: this.recipeType,
          ingredients: [],
          recipes: [],
          UOM: "",
          servings: null,
        },
      };
      this.recipeAltered = false;
    },

    async addNewRecipe() {
      if (this.recipeType === "prep" && (!("costing_type" in this.currRecipe.value) || this.currRecipe.value.costing_type == "")) {
        document.getElementById("cost-title").classList.add("border");
        return;
      } else {
        if (this.recipeType !== "prep") {
          this.currRecipe.value.costing_type = "menu";
          this.currRecipe.value.price_per_uom = "";
        } else {
          this.currRecipe.value.price_per_uom = (this.costing[4] / this.currRecipe.value.uom_quantity).toFixed(2) + " per " + this.currRecipe.value.UOM;
        }
        this.currRecipe.value.type = this.recipeType;
        this.saving = true;
        try { await api.add_cogs_doc(this.currRecipe); } catch (e) { console.warn("save_doc:", e); }
        this.saving = false;
        this.updateCogs("recipes");
        this.clearForm();
        this.$emit("recipe-saved");
        this.$emit("saved");
      }
    },

    async saveNewRecipe() {
      if (this.recipeType == "prep" && (!("costing_type" in this.currRecipe.value) || this.currRecipe.value.costing_type == "")) {
        document.getElementById("cost-title").classList.add("border");
        return;
      } else {
        if (this.recipeType !== "prep") {
          this.currRecipe.value.costing_type = "menu";
          this.currRecipe.value.price_per_uom = "";
        } else if (this.currRecipe.value.costing_type === "servings") {
          this.currRecipe.value.price_per_uom = (this.costing[1] / this.currRecipe.value.servings).toFixed(2) + " per serving";
          this.currRecipe.value.cost_per_serving = this.costing[1] / this.currRecipe.value.servings;
        } else {
          this.currRecipe.value.price_per_uom = (this.costing[4] / this.currRecipe.value.uom_quantity).toFixed(2) + " per " + this.currRecipe.value.UOM;
        }
        this.currRecipe.value.cost = this.costing[4];
        this.saving = true;
        try { await api.add_cogs_doc(this.currRecipe); } catch (e) { console.warn("save_doc:", e); }
        this.saving = false;
        this.updateCogs("recipes");
        this.clearForm();
        this.$emit("recipe-saved");
        this.$emit("saved");
      }
    },

    loadIngredient(ingredient) {
      if (!("ingredients" in this.currRecipe.value)) {
        this.currRecipe.value.ingredients = [];
      }
      this.currRecipe.value.ingredients.push({
        id: ingredient.id,
        usage: ingredient.usage,
        uom: ingredient.uom,
      });
    },

    loadSubRecipe(recipe) {
      if (!("recipes" in this.currRecipe.value)) {
        this.currRecipe.value.recipes = [];
      }
      this.currRecipe.value.recipes.push({
        id: recipe.id,
        usage: recipe.usage,
        uom: recipe.uom,
      });
    },

    removeIngredient(idx) {
      this.currRecipe.value.ingredients.splice(idx, 1);
    },

    removeRecipe(idx) {
      this.currRecipe.value.recipes.splice(idx, 1);
    },

  },

  watch: {
    currentRecipe: function () {
      if (!this.currentRecipe || !this.currentRecipe.value) return;
      this.currRecipe = JSON.parse(JSON.stringify(this.currentRecipe));
      this.recipeAltered = true;
      this.handleUnitChoice(this.currRecipe.value.costing_type);
    },
  },

};
</script>

<style lang="scss" scoped>
.prods-edit {
  max-height: 85vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 50%;
  margin-bottom: 1rem;
  flex-grow: 1;
  background-color: var(--primary-colour);
  box-shadow: var(--box-shadow);
  border-radius: 1rem;
  padding: 0.5rem;

  h2 {
    margin: 0.5rem;
  }

  .actions {
    display: flex;
    justify-content: space-between;

    button {
      height: 2.5rem;
      width: 11rem;
      margin: 0.5rem;
      padding: 0.5rem 1.5rem;
      background-color: var(--action-colour);
      border: none;
    }
    #left {
      background-color: var(--opacity-background);
      color: var(--text-colour);
    }
    #add {
      background-color: var(--primary-colour);
      border: 1px solid var(--action-colour);
      color: var(--action-colour);
    }
  }

  .totals {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: calc(100% - 2rem);
    padding: 1rem;
    p {
      margin: 0;
    }
  }

  form {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    overflow: auto;
    width: 95%;

    .list-container {
      min-height: 10vh;
      max-height: 40vh;
      width: 100%;
      overflow: auto;
      margin: 0.25rem 0;
      gap: 0.25rem;
      position: relative;
      overflow: auto;
      padding: 0.25rem 0;
      background-color: var(--opacity-background);
      border-radius: 0.5rem;
    }

    .border {
      box-shadow: var(--box-shadow);
    }
    #cost-title, .cost-title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      #cost {
        font-size: 0.9rem;
        color: var(--action-colour);
      }

      .selected {
        background-color: var(--secondary-colour);
        color: var(--text-colour);
        box-shadow: var(--box-shadow);
        border-radius: 0.25rem;
        padding: 0.25rem;
        margin: 0.25rem;
        cursor: pointer;
      }

      .opacity {
        opacity: 0.3;
      }

      .right-align {
        display: flex;
        justify-content: flex-end;
      }

      .unit {
        display: flex;
        align-items: center;
        width: 45%;
        cursor: pointer;

        .unit-block {
          display: flex;
          flex-direction: row;
          align-items: center;
          height: 3rem;
          input {
            width: 5rem;
            padding: 0.25rem;
            box-shadow: var(--box-shadow);
          }
        }
      }

      #small-radio {
        appearance: none;
        margin-right: 0.5rem;
        border-radius: 50%;
        transition: all 500ms ease-in-out;
        height: 1rem;
        width: 1rem;
        white-space: nowrap;
        border: 2px solid var(--tertiary-colour);
        &:checked {
          background-color: var(--action-colour);
          border: 5px solid var(--tertiary-colour);
        }
      }
      input {
        width: 3rem;
      }
      .one-label {
        margin: 0.5rem;
      }
      #cost {
        color: var(--action-colour);
        font-size: small;
      }
    }
    label {
      margin: 0.25rem;
    }

    input {
      padding: 0.25rem;
      margin: 0.1rem;
      box-shadow: var(--box-shadow);
      border-radius: 5px;
      border: none;
    }

    .ingredient-btn {
      display: flex;
      flex-direction: row;
      width: 95%;
      border-bottom: 1px solid var(--secondary-colour);
      align-items: center;
      justify-content: space-between;
      padding: 0 0.15rem;
      text-align: left;
      margin: 0 0.25rem;
      cursor: pointer;
      input {
        margin: 0 1rem;
      }
      #recipe-ing {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        #remove-icon {
          cursor: pointer;
        }
        .ingredient {
          display: flex;
          flex-direction: column;
          margin: 0.5rem 0.5rem;
          p {
            margin: 0;
            color: var(--text-colour);
            font-size: 0.8rem;
          }
          .subtitle {
            font-size: 0.7rem;
            font-style: italic;
            margin: 0;
            color: var(--action-colour);
          }
        }

        p {
          color: var(--text-colour);
        }
        span {
          font-size: 0.8rem;
          input {
            width: 65px;
            margin: 0;
            padding: 0.25rem;
          }
        }
      }
      svg {
        width: 20px;
        height: 20px;
        color: var(--warning-colour);
        margin: 0 0.5rem;
      }
    }
  }
  #add {
    max-height: 30vh;
    overflow: auto;
    margin: 0;
    padding: 0.25rem;

    > .extended {
      input {
        width: 50px;
        padding: 0.25rem;
        margin: 0.25rem;
      }
    }
    > .ingredient-btn {
      svg {
        background-color: var(--action-colour);
      }
    }
  }
}
@media only screen and (orientation: portrait) {
  .prods-edit {
    min-width: 90%;
    margin: 0 auto;
    padding: 1rem;
    .site-select {
      margin: 0 auto;
    }
    form {
      .unit-serving {
        #cost-title, .cost-title {
          width: 100%;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          #cost {
            margin-top: 0.5rem;
          }
          .unit {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            .radio-container {
              flex: 0 1 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .unit-block {
              display: flex;
              flex-direction: column;
              align-items: center;
              margin: 0.5rem;

              input {
                width: 3rem;
              }
            }
          }
          // .right-align {
          //   text-align: right;
          // }
        }
      }
    }
  }
  .prod-list {
    .search {
      ul {
        width: 85%;
      }
    }
  }
}
</style>
